
import { FaRegHeart, FaHeart } from "react-icons/fa";
import { Popover as AntdPopover, PopoverProps } from "antd";
import classNames from "classnames";

// components
import TooltipCampaignList from "@/components/TooltipCampaignList";
import ShortlistForm from "@/containers/ShortlistForm";
import Button from "@/shared/Button";

// helpers
import useAddToFavorite from "@/hooks/useAddToFavorite";
import useUserProfile from "@/hooks/useUserProfile";
import { useLockoutModalContext } from "@/contexts/LockoutModalContext";
import { useCompletedStepModalContext } from "@/contexts/CompletedStepModalContext";
import customDataLayerPush from "@/helpers/customDataLayerPush";

// interfaces
import { ISpaceFull } from "@/types/Space";

// assets
import styles from "@/assets/styles/pages/location-single-page/location-single-page-components.module.scss";
import shortlistFormStyles from "@/assets/styles/containers/shortlist-form.module.scss";
import { useHirerModalsContext } from "@/contexts/HirerModalsContext";
import { ILocationSpaceItem } from "@/types/api/Locations";


interface IPopover extends Omit<PopoverProps, "trigger"> {
  trigger?: "hover" | "focus" | "click" | "contextMenu" | "none";
  onClick: (e: React.MouseEvent) => void;
}

const Popover = AntdPopover as React.ForwardRefExoticComponent<IPopover & React.RefAttributes<unknown>>;

interface ILocationSpaceCardFavourite {
  campaigns: ILocationSpaceItem["campaigns"];
  popoverClassName?: string;
  isFavorite: boolean;
  fullSpaceData: ILocationSpaceItem;
  onAddToCampaign: () => void;
  handleSendAnalytics?: () => void;
}

const LocationSpaceCardFavourite = (props: ILocationSpaceCardFavourite) => {
  const {
    campaigns,
    popoverClassName,
    isFavorite,
    fullSpaceData,
    onAddToCampaign,
    handleSendAnalytics
  } = props;


  const { isOnlyOneCampaign, isZeroCampaign, handleToggleFavorite, isLoading } = useAddToFavorite({ isFavorite });
  const { isAuthorized, data: user } = useUserProfile();
  const { setState } = useLockoutModalContext();
  const { setState: completedStepModalSetState } = useCompletedStepModalContext();
  const { setState: openHirerModal, resetState } = useHirerModalsContext();

  const popoverClassNameToUse = classNames(shortlistFormStyles["locl-space-card-shortlist-popover"], popoverClassName);

  // methods
  const handlePopoverContentClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleShowCompletedStepModal = () => {
    if (user?.progressBar as number < 4) {
      completedStepModalSetState({
        open: true,
        type: "add-space",
        spaceSlug: fullSpaceData.slug,
        locationSlug: fullSpaceData.location.slug,
        progressBar: 4
      });
    }
  }

  const handleAddToFav = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.stopPropagation();

    if (!isAuthorized) {
      setState({
        open: true,
        loginCallback: handleSendAnalytics
      });
      customDataLayerPush("lo_fo_modal_popup_shown", null, { lofo_type: "soft lofo" });

      return;
    }

    if (isZeroCampaign) {
      openHirerModal({
        modalType: "firstCampaign",
        value: {
          open: true,
          spaceId: fullSpaceData.id,
          spaceName: fullSpaceData.name,
          onCancel: resetState,
          onSuccess: () => {
            handleShowCompletedStepModal();
            onAddToCampaign();
          },
        }
      })

      return;
    }

    isOnlyOneCampaign && handleToggleFavorite({
      spaceId: fullSpaceData.id,
      onSuccessAddToCampaign: handleShowCompletedStepModal,
    }).then(onAddToCampaign);
  };

  return (
    <TooltipCampaignList
      placement="right"
      campaigns={campaigns.map(campaign => campaign.id)}
      campaignsNames={campaigns.map(campaign => campaign.name)}
      isShowOnlyOnDesktop
      trigger="hover"
    >
      <Popover
        overlayClassName={popoverClassNameToUse}
        trigger={!isOnlyOneCampaign && isAuthorized && !isZeroCampaign ? "click" : "none"}
        content={(
          <ShortlistForm
            data-testid="locl-location-page-space-card-shortlist-form"
            space={fullSpaceData as unknown as ISpaceFull}
            onClick={handlePopoverContentClick}
          />
        )}
        placement="bottom"
        onClick={handleAddToFav}
        onOpenChange={(e) => !e && onAddToCampaign()}
      >
        <Button
          type="secondary"
          danger
          onClick={e => e.preventDefault()}
          icon={isFavorite
            ? <FaHeart data-testid="locl-space-card-shortlist-btn-fill-icon" />
            : <FaRegHeart data-testid="locl-space-card-shortlist-btn-not-fill-icon" />
          }
          className={classNames(
            styles["locl-location-page-space-card__favourite-btn"],
            { "locl-location-page-space-card__favourite-btn_active": isFavorite }, // need for E2E tests
          )}
          data-testid="locl-space-card-shortlist-btn" // don't modify this data-testid, it require for a/b tests
          loading={isLoading}
        >
          Add to favourites
        </Button>
      </Popover>
    </TooltipCampaignList>
  )
}

export default LocationSpaceCardFavourite;
